import type {
    AdministrativeArea,
    City,
    GeoCoordinates,
    PostalAddress,
} from 'schema-dts';
import type { LibraryLocation } from '@modules/search/type/hits';
import type { SearchLocation } from '@modules/search/type/api';
import { getMatchLocation } from '@modules/search/util/get-relevant-location';

/** Location type that represents a serviced area rather than a physical premises. */
const SERVICE_AREA_TYPE = 'ServiceArea';

/**
 * Premises types preferred (in order) when picking a representative address. Both Show Room
 * spellings are included because the source `Type` is `Showroom` while some indexed
 * `LocationType` values use `Show Room`.
 */
const PREMISES_PRIORITY = ['Head Office', 'Showroom', 'Show Room'];

/** Cap on `areaServed` entries to keep JSON-LD payload reasonable. */
const MAX_AREA_SERVED = 20;

/** Coordinates shape shared by the Search Service and the LibraryLocation GraphQL fragment. */
export interface SchemaMapCoordinates {
    Latitude?: number | null;
    Longitude?: number | null;
}

/** Physical premises only (excludes service areas). */
const physicalPremises = (locations: LibraryLocation[]): LibraryLocation[] =>
    locations.filter((loc) => loc.LocationType !== SERVICE_AREA_TYPE);

/**
 * Pick the single most representative premises for schema address/geo. Prefers a
 * premises matching the page's applied location filter, then Head Office, then
 * Show Room, then the first physical premises. Returns undefined for a
 * service-area-only professional (no physical premises).
 */
export const selectPrimaryPremises = (
    locations: LibraryLocation[] | undefined,
    appliedLocationFilter?: SearchLocation,
): LibraryLocation | undefined => {
    if (!locations?.length) return undefined;

    const premises = physicalPremises(locations);
    if (!premises.length) return undefined;

    if (appliedLocationFilter) {
        const matched = getMatchLocation(premises, appliedLocationFilter, true);
        if (matched && matched.LocationType !== SERVICE_AREA_TYPE) {
            return matched;
        }
    }

    for (const type of PREMISES_PRIORITY) {
        const found = premises.find((loc) => loc.LocationType === type);
        if (found) return found;
    }

    return premises[0];
};

/**
 * Trim a value and return undefined when it is empty or whitespace-only, so we never
 * emit blank or padded JSON-LD fields (e.g. a `telephone` of "   " or " 09 ... ").
 */
export const cleanString = (value?: string | null): string | undefined => {
    const trimmed = value?.trim();
    return trimmed ? trimmed : undefined;
};

const samePlaceName = (left?: string, right?: string): boolean =>
    !!left && !!right && left.toLowerCase() === right.toLowerCase();

/** Minimal premises fields needed to render a short locality label. */
export interface LocalitySource {
    Suburb?: string | null;
    City?: string | null;
    /** County / admin2 name, used for servicing labels on division pages. */
    Division?: string | null;
    Region?: string | null;
    State?: string | null;
}

/**
 * Short locality label for a premises, e.g. "Parnell, Auckland". Prefers Suburb over
 * City over Division, and Region over State, so directory tiles show an area rather
 * than a full street address. The admin area is dropped when it repeats the locality,
 * so a metro region named after its own city reads "Auckland", not "Auckland, Auckland".
 * Returns an empty string when no locality parts are present.
 */
export const formatLocalityLabel = (premises: LocalitySource): string => {
    const locality =
        cleanString(premises.Suburb) ??
        cleanString(premises.City) ??
        cleanString(premises.Division);
    const adminArea =
        cleanString(premises.Region) ?? cleanString(premises.State);

    return [
        locality,
        samePlaceName(locality, adminArea) ? undefined : adminArea,
    ]
        .filter((value): value is string => !!value)
        .join(', ');
};

/** Short locality label for a physical premises, e.g. "Parnell, Auckland". */
const formatPremisesLabel = (premises: LibraryLocation): string =>
    formatLocalityLabel(premises);

/** Prefix for a serviceable-area tile label, e.g. "Services Los Angeles, California". */
const SERVICING_LABEL_PREFIX = 'Services';

/**
 * Prefixed locality for a firm that services the page place but has no office there.
 * Uses the same two-part locality as premises labels (city + region/state) so same-named
 * places in different admin areas stay distinct. A state or region page with no city
 * reads "Services California".
 */
const formatServicingLocationLabel = (location: SearchLocation): string => {
    const place = formatLocalityLabel({
        Suburb: location.suburb,
        City: location.city,
        Division: location.division,
        Region: location.region,
        State: location.state,
    });
    return place ? `${SERVICING_LABEL_PREFIX} ${place}` : '';
};

/**
 * The page's admin value for matching, finest grain first. US pages store the state
 * in Region with State empty; AU places fill State with Region empty; NZ pages carry
 * both at different grains (Region = council, State = island).
 */
const adminAreaFromSearch = (location: SearchLocation): string | undefined =>
    cleanString(location.region) ?? cleanString(location.state);

const adminAreaFromLocality = (premises: LocalitySource): string | undefined =>
    cleanString(premises.Region) ?? cleanString(premises.State);

const premisesCityMatches = (
    premises: LocalitySource,
    pageCity: string,
): boolean =>
    samePlaceName(pageCity, cleanString(premises.City)) ||
    samePlaceName(pageCity, cleanString(premises.Suburb));

/**
 * True when the page's admin value names either premises admin field. NZ premises
 * carry Region (council) and State (island) at different grains, so an island
 * (state-only) page must match the premises State even though Region is filled.
 * The page side stays a single value — matching the page's coarser state against
 * premises would pin every same-island office on a region page.
 */
const premisesAdminEquals = (
    premises: LocalitySource,
    pageAdmin: string,
): boolean =>
    samePlaceName(pageAdmin, cleanString(premises.Region)) ||
    samePlaceName(pageAdmin, cleanString(premises.State));

const premisesMatchesAdmin = (
    premises: LocalitySource,
    pageAdmin?: string,
): boolean => {
    const locAdmin = adminAreaFromLocality(premises);
    return !pageAdmin || !locAdmin || premisesAdminEquals(premises, pageAdmin);
};

const divisionCitiesFromSearch = (location: SearchLocation): string[] =>
    (location.divisionCities ?? [])
        .map((city) => cleanString(city))
        .filter((city): city is string => !!city);

/**
 * Physical premises in the applied place. The page's admin value (region, falling
 * back to state) must name either premises admin field — see `premisesAdminEquals`
 * for the per-market shapes. City pages require a city or suburb match (and matching
 * admin areas when both sides have one). County (division) pages pin only an office
 * in one of `divisionCities` — premises store a city, not the county, so a same-state
 * head office is not a match. State or region pages match on admin area only.
 */
const findPremisesInAppliedLocation = (
    locations: LibraryLocation[] | undefined,
    appliedLocation: SearchLocation,
): LibraryLocation | undefined => {
    const premises = physicalPremises(locations ?? []);
    if (!premises.length) return undefined;

    const pageCity =
        cleanString(appliedLocation.suburb) ??
        cleanString(appliedLocation.city);
    const pageAdmin = adminAreaFromSearch(appliedLocation);
    const pageDivision = cleanString(appliedLocation.division);
    const divisionCities = divisionCitiesFromSearch(appliedLocation);

    if (pageCity) {
        return premises.find(
            (loc) =>
                premisesCityMatches(loc, pageCity) &&
                premisesMatchesAdmin(loc, pageAdmin),
        );
    }

    if (pageDivision) {
        if (!divisionCities.length) return undefined;
        return premises.find(
            (loc) =>
                divisionCities.some((city) => premisesCityMatches(loc, city)) &&
                premisesMatchesAdmin(loc, pageAdmin),
        );
    }

    if (!pageAdmin) return undefined;
    return premises.find((loc) => premisesAdminEquals(loc, pageAdmin));
};

/** Visible location line on a directory tile, plus whether it may show a map pin. */
export interface TileLocationDisplay {
    label: string;
    /**
     * True when `label` is a physical premises. Serviceable-area labels must not
     * render with a map pin — a pin plus a locality reads as "this firm is here".
     */
    showPin: boolean;
}

const emptyTileLocation = (): TileLocationDisplay => ({
    label: '',
    showPin: false,
});

/**
 * Location line shown on a directory tile.
 *
 * On a category × location page (`appliedLocation` set) the label reflects the
 * page's target location, in every market. A physical premises in that city (or
 * in the county's cities, or in the admin area on a state/region page) keeps the
 * pin and locality. Otherwise the label is a prefixed serviceable-area claim with
 * no pin, so a professional that only services the area never appears to be
 * located there. Matching treats Region and State as the same admin field; it
 * does not use `getMatchLocation` (which also requires a filled State and
 * eager-matches other cities in the same state).
 *
 * Without an applied location we keep the representative-premises behaviour from
 * `selectPrimaryPremises`.
 */
export const resolveTileLocation = (
    locations: LibraryLocation[] | undefined,
    appliedLocation?: SearchLocation,
): TileLocationDisplay => {
    if (appliedLocation) {
        const premisesMatch = findPremisesInAppliedLocation(
            locations,
            appliedLocation,
        );
        const premisesLabel = premisesMatch
            ? formatPremisesLabel(premisesMatch)
            : '';
        if (premisesLabel) {
            return { label: premisesLabel, showPin: true };
        }
        const servicingLabel = formatServicingLocationLabel(appliedLocation);
        return servicingLabel
            ? { label: servicingLabel, showPin: false }
            : emptyTileLocation();
    }

    const premises = selectPrimaryPremises(locations);
    return premises
        ? { label: formatPremisesLabel(premises), showPin: true }
        : emptyTileLocation();
};

/** Build a schema.org PostalAddress from a premises. */
export const buildPostalAddress = (
    premises: LibraryLocation,
    defaultCountry?: string,
): PostalAddress => {
    const streetAddress = [premises.Address1, premises.Address2]
        .map(cleanString)
        .filter((value): value is string => !!value)
        .join(', ');
    const addressLocality =
        cleanString(premises.Suburb) ?? cleanString(premises.City);
    // Fall back to the coarser State when a premises has no Region (mirrors buildAreaServed).
    const addressRegion =
        cleanString(premises.Region) ?? cleanString(premises.State);
    const addressCountry =
        cleanString(premises.Country) ?? cleanString(defaultCountry);
    const postalCode = cleanString(premises.PostCode);
    const telephone = cleanString(premises.Phone);

    return {
        '@type': 'PostalAddress',
        ...(streetAddress && { streetAddress }),
        ...(addressLocality && { addressLocality }),
        ...(addressRegion && { addressRegion }),
        ...(postalCode && { postalCode }),
        ...(addressCountry && { addressCountry }),
        ...(telephone && { telephone }),
    };
};

/** Valid geographic coordinate bounds. */
const MIN_LATITUDE = -90;
const MAX_LATITUDE = 90;
const MIN_LONGITUDE = -180;
const MAX_LONGITUDE = 180;

/**
 * True when coordinates are finite numbers within valid geographic ranges and
 * not the `(0,0)` "null island" default. Ungeocoded premises persist as
 * `(0,0)` — the source `MapCoordinates` is a non-null `Coordinate!` backed by a
 * `Decimal` column defaulting to 0 — which would otherwise render a `geo` pin in
 * the ocean off West Africa. `(0,0)` is itself in range, so it needs an explicit
 * check alongside the bounds check.
 */
export const hasValidCoordinates = (
    coordinates?: SchemaMapCoordinates | null,
): coordinates is { Latitude: number; Longitude: number } => {
    const latitude = coordinates?.Latitude;
    const longitude = coordinates?.Longitude;
    if (typeof latitude !== 'number' || typeof longitude !== 'number') {
        return false;
    }
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return false;
    }
    if (latitude === 0 && longitude === 0) {
        return false;
    }
    return (
        latitude >= MIN_LATITUDE &&
        latitude <= MAX_LATITUDE &&
        longitude >= MIN_LONGITUDE &&
        longitude <= MAX_LONGITUDE
    );
};

/**
 * Build GeoCoordinates from map coordinates, when they are valid (finite, in
 * range, not `(0,0)`). Returns undefined otherwise so we never emit a pin for an
 * ungeocoded premises.
 */
export const buildGeo = (
    coordinates?: SchemaMapCoordinates | null,
): GeoCoordinates | undefined => {
    if (!hasValidCoordinates(coordinates)) {
        return undefined;
    }

    return {
        '@type': 'GeoCoordinates',
        latitude: coordinates.Latitude,
        longitude: coordinates.Longitude,
    };
};

/**
 * Build a de-duplicated, capped `areaServed` list from a professional's
 * service-area locations. City/suburb maps to City; a broader region/state maps
 * to AdministrativeArea.
 */
export const buildAreaServed = (
    locations: LibraryLocation[] | undefined,
): Array<City | AdministrativeArea> => {
    if (!locations?.length) return [];

    const seen = new Set<string>();
    const areas: Array<City | AdministrativeArea> = [];

    for (const loc of locations) {
        if (loc.LocationType !== SERVICE_AREA_TYPE) continue;

        const cityName = cleanString(loc.City) ?? cleanString(loc.Suburb);
        const name =
            cityName ?? cleanString(loc.Region) ?? cleanString(loc.State);
        if (!name || seen.has(name)) continue;

        seen.add(name);
        areas.push(
            cityName
                ? { '@type': 'City', name }
                : { '@type': 'AdministrativeArea', name },
        );

        if (areas.length >= MAX_AREA_SERVED) break;
    }

    return areas;
};

/**
 * LocalBusiness when the professional has a real premises (with address/geo),
 * otherwise Organization so we never claim a postal address we do not have.
 */
export const pickProfessionalType = (
    premises: LibraryLocation | undefined,
): 'LocalBusiness' | 'Organization' =>
    premises ? 'LocalBusiness' : 'Organization';
